import React, { useEffect, useState } from 'react';
import { getAccount, CATEGORIES } from '../api';
import { supabase } from '../supabase';

export default function AccountPanel({ onPurchase, isMobile }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAccount()
      .then(setAccount)
      .catch(() => setAccount(null))
      .finally(() => setLoading(false));
  }, []);

  const handleSignOut = () => supabase.auth.signOut();

  const categoryLabel = (key) => {
    const cat = CATEGORIES.find(c => c.key === key);
    return cat ? `${cat.icon} ${cat.label}` : key;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.title}>Account</div>
        <div style={styles.empty}>Loading...</div>
      </div>
    );
  }

  if (!account) {
    return (
      <div style={styles.container}>
        <div style={styles.title}>Account</div>
        <div style={styles.empty}>Unable to load account details.</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.title}>Account</div>

      <div>

          {/* Credits */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>Report Credits</div>
            <div style={styles.creditsRow}>
              <div style={styles.creditCard}>
                <div style={styles.creditValue}>{account.credits}</div>
                <div style={styles.creditLabel}>Quick Reports</div>
                <div style={styles.creditPrice}>R15,000 each</div>
              </div>
              <div style={styles.creditCard}>
                <div style={styles.creditValue}>{account.sc_credits}</div>
                <div style={styles.creditLabel}>SC Feasibility</div>
                <div style={styles.creditPrice}>R45,000 each</div>
              </div>
            </div>
            {onPurchase && (
              <button onClick={onPurchase} style={styles.purchaseBtn}>
                Fund Account
              </button>
            )}
          </div>

          {/* Profile */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>Profile</div>
            <div style={styles.fieldGrid}>
              <ProfileField label="Name" value={[account.first_name, account.last_name].filter(Boolean).join(' ')} />
              <ProfileField label="Email" value={account.email} mono />
              <ProfileField label="Company" value={account.company} />
              <ProfileField label="Brand" value={account.brand} />
              {account.admin && (
                <div style={styles.adminBadge}>Admin</div>
              )}
            </div>
          </div>

          {/* Categories */}
          {account.retail_categories?.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionLabel}>Retail Categories</div>
              <div style={styles.catList}>
                {account.retail_categories.map(key => (
                  <span key={key} style={styles.catChip}>{categoryLabel(key)}</span>
                ))}
              </div>
            </div>
          )}

          {/* Sign out */}
          <div style={{ ...styles.section, borderBottom: 'none' }}>
            <button onClick={handleSignOut} style={styles.signOutBtn}>Sign out</button>
          </div>
      </div>
    </div>
  );
}

function ProfileField({ label, value, mono }) {
  return (
    <div style={styles.field}>
      <div style={styles.fieldLabel}>{label}</div>
      <div style={{ ...styles.fieldValue, ...(mono ? { fontFamily: "'DM Mono', monospace", fontSize: 12 } : {}) }}>
        {value || '—'}
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    background: 'var(--cream)',
    padding: '24px',
  },
  title: {
    fontFamily: "'News Cycle', serif",
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--ink)',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottom: '1px solid var(--rule)',
  },
  empty: {
    textAlign: 'center',
    padding: '40px 20px',
    color: 'var(--muted)',
    fontSize: 13,
  },
  section: {
    marginBottom: 24,
    paddingBottom: 24,
    borderBottom: '1px solid var(--rule)',
  },
  sectionLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    marginBottom: 14,
  },
  creditsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    marginBottom: 16,
  },
  creditCard: {
    background: 'var(--white)',
    border: '1px solid var(--rule)',
    borderRadius: 6,
    padding: '20px 16px',
    textAlign: 'center',
  },
  creditValue: {
    fontFamily: "'News Cycle', serif",
    fontSize: 36,
    fontWeight: 700,
    color: 'var(--ink)',
    lineHeight: 1,
  },
  creditLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--ink)',
    marginTop: 8,
  },
  creditPrice: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    color: 'var(--muted)',
    marginTop: 4,
  },
  purchaseBtn: {
    width: '100%',
    padding: '12px',
    background: 'var(--accent)',
    color: '#14110e',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Work Sans', sans-serif",
    letterSpacing: '0.3px',
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  field: {
    background: 'var(--white)',
    border: '1px solid var(--rule)',
    borderRadius: 6,
    padding: '12px 16px',
  },
  fieldLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    marginBottom: 4,
  },
  fieldValue: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--ink)',
  },
  adminBadge: {
    background: 'rgba(255,106,61,0.1)',
    color: 'var(--accent)',
    border: '1px solid var(--accent)',
    borderRadius: 6,
    padding: '12px 16px',
    fontFamily: "'DM Mono', monospace",
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  catList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  catChip: {
    padding: '6px 10px',
    background: 'var(--white)',
    border: '1px solid var(--rule)',
    borderRadius: 16,
    fontSize: 11,
    color: 'var(--ink)',
    fontFamily: "'Work Sans', sans-serif",
  },
  signOutBtn: {
    width: '100%',
    padding: '10px',
    background: 'transparent',
    border: '1px solid var(--rule)',
    borderRadius: 6,
    fontSize: 13,
    color: 'var(--muted)',
    cursor: 'pointer',
    fontFamily: "'Work Sans', sans-serif",
  },
};
